import { useEffect, useState } from 'react';
import { getSupabase } from '../client';
import { MfaEnrollWizard } from './MfaEnrollWizard';

interface Props {
  /** Llamado cuando la sesión queda upgradeada a aal2 (MFA reciente). */
  onSuccess: () => void;
  onCancel: () => void;
  /**
   * Label del botón de cancelación. Default: 'Cancelar'. AuthGate pasa
   * 'Cerrar sesión' porque cuando es forzado (`forceEnroll` o TTL expired)
   * no hay forma de continuar sin MFA — onCancel hace signOut.
   */
  cancelLabel?: string;
}

type Phase = 'detect' | 'enroll' | 'challenge' | 'verifying' | 'done' | 'error';

/**
 * Modal para "desbloquear" sesión a aal2 sin logout.
 *
 * Flujo:
 *   1. detect: lista factors del user
 *      - 0 verified → enroll: monta MfaEnrollWizard (crea factor + verify primer código)
 *      - >=1 verified → challenge: pide código TOTP actual y llama mfa.challenge + verify
 *   2. onSuccess → caller retrying la operación que necesitaba aal2
 *
 * Sin re-login. Cliente Elawan u Aosenuma puede entrar PII sin perder la
 * sesión en otra pestaña.
 */
export function MfaUnlockModal({ onSuccess, onCancel, cancelLabel = 'Cancelar' }: Props) {
  const [phase, setPhase] = useState<Phase>('detect');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // El Web Lock interno de gotrue-js (`lock:sb-...-auth-token`) puede colgar
    // listFactors hasta 5-10s si otro componente lo tiene tomado bajo React
    // Strict Mode o doble-mount. Bug observado en e2e (Playwright) donde el
    // primer mount adquiría el lock y el segundo timeout-eaba → fallback
    // incorrecto a enroll wizard, bloqueando la hidratación del form.
    //
    // Estrategia: cache del factor_id verificado en localStorage para evitar
    // depender de listFactors en cargas subsiguientes. Si no hay cache,
    // retry con backoff (5s → 10s → 15s) antes de caer a enroll. Solo
    // cuando los 3 intentos fallan asumimos "no enrolled".
    //
    // El cache es seguro: el factor_id no es secreto, solo identifica el
    // factor MFA. Lo invalidamos cuando un challenge falla con factor_invalid.
    //
    // SCOPING: el cache key incluye el userId. Bug previo (pre-2026-05-11):
    //   el cache era global por dominio. Si user A se logeaba y cacheaba su
    //   factor, y luego user B reusaba el browser, el modal pegaba el factor
    //   de A → Supabase respondía 422 (factor mismatch). El handler de error
    //   solo cubría 404/factor-not-found, así el cache stale quedaba atrapado
    //   y el user B nunca podía verificar.
    const CACHE_KEY_PREFIX = 'aos.mfa.factor_id.';
    // Legacy key sin scoping — lo limpiamos al cargar para evitar arrastrar
    // cache contaminado de sesiones previas. Es seguro borrar incondicional:
    // si el factor es del user correcto, listFactors lo recachea con scope.
    try { window.localStorage.removeItem('aos.mfa.factor_id'); } catch { /* */ }

    function getCachedFactorId(uid: string | null): string | null {
      if (!uid) return null;
      try {
        const raw = window.localStorage.getItem(CACHE_KEY_PREFIX + uid);
        return raw && /^[a-f0-9-]{36}$/i.test(raw) ? raw : null;
      } catch { return null; }
    }
    function setCachedFactorId(uid: string, id: string) {
      try { window.localStorage.setItem(CACHE_KEY_PREFIX + uid, id); } catch { /* private mode */ }
    }

    async function tryListFactors(timeoutMs: number) {
      const sb = getSupabase();
      return Promise.race([
        sb.auth.mfa.listFactors(),
        new Promise<{ data: null; error: Error }>((res) =>
          window.setTimeout(
            () => res({ data: null, error: new Error(`listFactors timeout ${timeoutMs}ms`) }),
            timeoutMs,
          ),
        ),
      ]);
    }

    void (async () => {
      try {
        // Resolver userId actual ANTES de tocar cache. Usamos getSession()
        // y NO getUser() para evitar tocar el Web Lock interno de gotrue-js
        // cuando ya hay un session válido cacheado:
        //   - getSession(): lee del storage local, lock-free cuando JWT no
        //     necesita refresh. Bajo React Strict Mode (doble-mount) esto
        //     evita el warning "Lock was not released within 5000ms" que
        //     Diego observó 2026-05-11.
        //   - getUser(): siempre intenta hit al servidor /auth/v1/user para
        //     validar el JWT, lo cual toma el lock y suele colgar bajo
        //     doble-mount.
        // Si el JWT está expirado, getSession devuelve null user → caemos a
        // error y AuthGate hará signOut.
        const sb = getSupabase();
        const { data: sessData, error: sessErr } = await sb.auth.getSession();
        if (cancelled) return;
        if (sessErr || !sessData?.session?.user) {
          setErr('Sesión no válida. Vuelve a iniciar sesión.');
          setPhase('error');
          return;
        }
        const uid = sessData.session.user.id;

        // Fast path: factor_id cacheado de una sesión previa exitosa del
        // mismo user (scope por uid). Si el factor fue revocado entre
        // sesiones, el challenge fallará y limpiamos el cache (en
        // submitChallenge).
        const cached = getCachedFactorId(uid);
        if (cached) {
          setFactorId(cached);
          setPhase('challenge');
          return;
        }

        // Slow path con retry: 5s, 10s, 15s.
        const timeouts: readonly number[] = [5000, 10000, 15000];
        for (let i = 0; i < timeouts.length; i++) {
          if (cancelled) return;
          const result = await tryListFactors(timeouts[i] ?? 5000);
          if (cancelled) return;
          const { data, error } = result as Awaited<ReturnType<ReturnType<typeof getSupabase>['auth']['mfa']['listFactors']>>;
          if (!error) {
            const totp = data?.totp ?? [];
            const verified = totp.find((f) => f.status === 'verified');
            if (verified) {
              setCachedFactorId(uid, verified.id);
              setFactorId(verified.id);
              setPhase('challenge');
            } else {
              setPhase('enroll');
            }
            return;
          }
          // eslint-disable-next-line no-console
          console.warn(`[MfaUnlockModal] listFactors retry ${i + 1}/${timeouts.length} falló: ${error.message}`);
        }

        // 3 retries fallaron → asumimos enroll. La probabilidad de que
        // un user real tenga lock contention durante 30s es muy baja.
        // eslint-disable-next-line no-console
        console.warn('[MfaUnlockModal] listFactors falló tras 3 retries — fallback a enroll wizard');
        if (!cancelled) setPhase('enroll');
      } catch (e) {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : String(e));
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function submitChallenge() {
    if (!factorId) return;
    if (!/^\d{6}$/.test(code.trim())) {
      setErr('Código de 6 dígitos requerido.');
      return;
    }
    setErr(null);
    setPhase('verifying');
    try {
      const sb = getSupabase();
      const { data: ch, error: chErr } = await sb.auth.mfa.challenge({ factorId });
      if (chErr || !ch) throw chErr ?? new Error('challenge falló');
      const { error: vErr } = await sb.auth.mfa.verify({
        factorId,
        challengeId: ch.id,
        code: code.trim(),
      });
      if (vErr) throw vErr;
      // Registrar el evento de consentimiento PII en bitácora (mig 0067).
      // El acto positivo de teclear el TOTP exitoso constituye consentimiento
      // informado bajo LFPDPPP — el RPC inserta `session.pii_unlock_granted`
      // en `eventos_negociacion`. Idempotente dentro de 8h: si ya hay evento
      // reciente (re-prompt por TTL), el RPC devuelve idempotent_hit=true.
      //
      // Tolerante a fallos: si el RPC falla (offline, rate limit, error DB),
      // NO bloqueamos el flow del usuario. Solo log warn — el unlock TOTP
      // ya pasó y la sesión queda aal2. Audit log es nice-to-have, no
      // critical-path.
      try {
        // El parámetro del RPC mantiene el nombre `p_aviso_version` por
        // compatibilidad con la migración 0067; el valor ahora referencia
        // 'sla_v1' (documento SLA publicado en /sla del landing) en lugar
        // del aviso de privacidad anterior. Old events con aviso_version='v1'
        // permanecen en bitácora como audit histórico.
        const { error: auditErr } = await sb.rpc('record_session_pii_unlock', {
          p_aviso_version: 'sla_v1',
        });
        if (auditErr) {
          console.warn('[MfaUnlockModal] record_session_pii_unlock fallo (no-blocking):', auditErr.message);
        }
      } catch (auditCatch) {
        console.warn('[MfaUnlockModal] record_session_pii_unlock exception (no-blocking):', auditCatch);
      }
      setPhase('done');
      onSuccess();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // Distinguir dos clases de fallo:
      //
      //   A) Código TOTP equivocado/expirado — el factor es válido pero el
      //      código no matchea. Patterns típicos: "invalid totp code",
      //      "invalid code", "verification failed". User retypes; NO limpiar
      //      cache.
      //
      //   B) Factor stale o mismatch — el factor cacheado fue revocado, o
      //      pertenece a otro user (cache contaminado entre sesiones del
      //      mismo browser pre-fix scoping). Supabase Auth devuelve:
      //        - 404 / "factor not found" — factor borrado server-side
      //        - 422 con mensajes como "MFA factor not associated with this
      //          user" o "factor does not belong to current user" — factor
      //          existe pero es de otro user. ← causa raíz del bug Diego
      //      En ambos casos: limpiar cache scoped y volver a detect para
      //      hacer listFactors fresh del user actual.
      const isFactorStale =
        /factor.*not.*found|invalid.*factor|not.*associated|not.*belong|does.*not.*belong|wrong.*factor/i.test(msg) ||
        / 404 | 404\b/.test(msg);
      if (isFactorStale) {
        try {
          // Limpiar TODOS los keys del prefix — agresivo pero seguro: el
          // listFactors regenera el cache correcto. Mejor sobre-purgar que
          // dejar un key contaminado oculto.
          for (let i = window.localStorage.length - 1; i >= 0; i--) {
            const k = window.localStorage.key(i);
            if (k && (k === 'aos.mfa.factor_id' || k.startsWith('aos.mfa.factor_id.'))) {
              window.localStorage.removeItem(k);
            }
          }
        } catch { /* private mode / SSR */ }
        setFactorId(null);
        setPhase('detect');
        return;
      }
      setErr(msg);
      setPhase('challenge');
    }
  }

  return (
    <div style={overlayStyle()} role="dialog" aria-modal="true">
      <div style={cardStyle()}>
        {phase === 'detect' && (
          <>
            <h3 style={titleStyle}>Verificación adicional</h3>
            <p style={pStyle}>Comprobando tu segundo factor…</p>
          </>
        )}

        {phase === 'enroll' && (
          <MfaEnrollWizard
            onSuccess={() => onSuccess()}
            onCancel={onCancel}
            issuer="Aosenuma"
            friendlyName="Aosenuma"
            cancelLabel={cancelLabel}
          />
        )}

        {(phase === 'challenge' || phase === 'verifying') && (
          <>
            <h3 style={titleStyle}>Verifica tu código TOTP</h3>
            <p style={pStyle}>
              Ingresa tu código de 6 dígitos (Google Authenticator, 1Password, etc.).
            </p>
            <p style={disclaimerStyle}>
              Al continuar aceptas el <strong>SLA</strong> y autorizas ver nombres
              completos durante 8 horas. Cada acceso queda en bitácora.
            </p>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              style={inputStyle}
              disabled={phase === 'verifying'}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitChallenge();
              }}
            />
            {err && <p style={errStyle}>{err}</p>}
            <div style={btnRowStyle}>
              <button type="button" style={btnGhostStyle} onClick={onCancel} disabled={phase === 'verifying'}>
                {cancelLabel}
              </button>
              <button
                type="button"
                style={btnPrimaryStyle}
                onClick={() => void submitChallenge()}
                disabled={phase === 'verifying' || code.length !== 6}
              >
                {phase === 'verifying' ? 'Verificando…' : 'Verificar'}
              </button>
            </div>
          </>
        )}

        {phase === 'error' && (
          <>
            <h3 style={titleStyle}>Error</h3>
            <p style={errStyle}>{err ?? 'Error desconocido.'}</p>
            <div style={btnRowStyle}>
              <button type="button" style={btnGhostStyle} onClick={onCancel}>
                Cerrar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---- Estilos inline (modal aislado, no depende de la app) ---- */
const overlayStyle = (): React.CSSProperties => ({
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.55)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  padding: 16,
});
const cardStyle = (): React.CSSProperties => ({
  background: 'var(--bg-deep, #0c1216)',
  color: 'var(--text, #f2f4ed)',
  border: '1px solid var(--glass-border, rgba(255,255,255,0.12))',
  borderRadius: 12,
  padding: '24px 28px',
  maxWidth: 460,
  width: '100%',
  boxShadow: '0 12px 40px rgba(0,0,0,0.36)',
  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
});
const titleStyle: React.CSSProperties = {
  margin: '0 0 8px',
  fontSize: 16,
  fontWeight: 600,
};
// Subimos opacidad del párrafo principal de 0.7 → 0.92 para contraste WCAG
// AA contra el fondo #0c1216. Antes la línea "Ingresa tu código…" era casi
// ilegible bajo monitores brillantes (Diego 2026-05-11).
const pStyle: React.CSSProperties = { margin: '0 0 12px', fontSize: 13, color: 'rgba(242,244,237,0.92)', lineHeight: 1.5 };
// Disclaimer secundario un peldaño más bajo que el copy principal pero
// todavía legible (no var(--text-mid) que apenas se leía en producción).
const disclaimerStyle: React.CSSProperties = { margin: '-4px 0 12px', fontSize: 12, color: 'rgba(242,244,237,0.70)', lineHeight: 1.5 };
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  fontSize: 18,
  letterSpacing: '0.4em',
  textAlign: 'center',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid var(--glass-border, rgba(255,255,255,0.18))',
  borderRadius: 8,
  color: 'var(--text, #f2f4ed)',
  marginBottom: 8,
  outline: 'none',
  fontFamily: 'monospace',
};
const errStyle: React.CSSProperties = { color: '#dc2626', fontSize: 12, margin: '4px 0 12px' };
const btnRowStyle: React.CSSProperties = { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 };
const btnGhostStyle: React.CSSProperties = {
  padding: '8px 16px',
  background: 'transparent',
  color: 'var(--text-mid, rgba(242,244,237,0.7))',
  border: '1px solid var(--glass-border, rgba(255,255,255,0.18))',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
};
const btnPrimaryStyle: React.CSSProperties = {
  padding: '8px 16px',
  background: 'var(--btn-primary-bg, #b4c96f)',
  color: '#0c1216',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
